import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import { cloneElement, Children, createElement } from 'react';
import { extension, ExtensionPriority, PlainExtension, object, isArray } from '@remirror/core';
import { isReactFragment, getElementProps, isReactDOMElement } from '@remirror/react-utils';

var _dec, _class;
var ReactSsrExtension = (_dec = extension({
  defaultOptions: {
    transformers: [injectBrIntoEmptyParagraphs]
  },
  defaultPriority: ExtensionPriority.High,
  staticKeys: ['transformers']
}), _dec(_class = class ReactSsrExtension extends PlainExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "createSSRTransformer", () => initialElement => {
      var element = initialElement;

      var _iterator = _createForOfIteratorHelper(this.options.transformers),
          _step;

      try {
        for (_iterator.s(); !(_step = _iterator.n()).done;) {
          var transformer = _step.value;
          element = transformer(element);
        }
      } catch (err) {
        _iterator.e(err);
      } finally {
        _iterator.f();
      }

      return element;
    });
  }

  get name() {
    return 'reactSsr';
  }

  onCreate() {
    var components = object();
    var ssrTransformers = [];

    var ssrTransformer = (initialElement, state) => {
      var element = initialElement;

      var _iterator2 = _createForOfIteratorHelper(ssrTransformers),
          _step2;

      try {
        for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
          var transformer = _step2.value;
          element = transformer()(element, state);
        }
      } catch (err) {
        _iterator2.e(err);
      } finally {
        _iterator2.f();
      }

      return element;
    };

    var _iterator3 = _createForOfIteratorHelper(this.store.extensions),
        _step3;

    try {
      for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
        var _this$store$managerSe, _extension$options$ex, _extension$options$ex2;

        var _extension = _step3.value;

        if (!((_this$store$managerSe = this.store.managerSettings.exclude) !== null && _this$store$managerSe !== void 0 && _this$store$managerSe.reactSSR) && _extension.ReactComponent && _extension.reactComponentEnvironment !== 'dom' && !((_extension$options$ex = _extension.options.exclude) !== null && _extension$options$ex !== void 0 && _extension$options$ex.reactSSR)) {
          components[_extension.name] = {
            Component: _extension.ReactComponent,
            props: {
              decorations: [],
              environment: 'ssr',
              forwardRef: () => {},
              getPosition: () => 0,
              selected: false,
              updateAttributes: () => {}
            }
          };
        }

        if (_extension.createSSRTransformer && !((_extension$options$ex2 = _extension.options.exclude) !== null && _extension$options$ex2 !== void 0 && _extension$options$ex2.reactSSR)) {
          ssrTransformers.push(_extension.createSSRTransformer.bind(_extension));
        }
      }
    } catch (err) {
      _iterator3.e(err);
    } finally {
      _iterator3.f();
    }

    this.store.setStoreKey('components', components);
    this.store.setStoreKey('ssrTransformer', ssrTransformer);
  }
  /**
   * A function that takes in the initial automatically produced JSX by the
   * ReactSSRSerializer and transforms it into and element that is consistent
   * between the browser and the server.
   */


}) || _class);

/**
 * Clone SSR elements ignoring the top level Fragment
 *
 * @remarks
 * A utility method for the SSR JSX
 *
 * @param element - the element to transform which must be from the JSX received
 * in `ssrTransformer`
 * @param transformChildElements - receives the nested elements and props and
 * transforms them into another JSX.Element
 */
function cloneSSRElement(element, transformChildElements) {
  if (!isReactFragment(element)) {
    throw new Error('Invalid element passed. The top level element must be a fragment');
  }

  var _getElementProps = getElementProps(element),
      children = _getElementProps.children;

  var childrenProperties = getElementProps(children);
  return /*#__PURE__*/cloneElement(element, {}, transformChildElements(children, childrenProperties));
}
/**
 * Returns true when a react element has no children.
 *
 * @param element - the element to test
 */


function elementIsEmpty(element) {
  return Children.count(element.props.children) === 0;
}
/**
 * Checks to see that the element is of the provided type.
 *
 * @param element - the element to test
 * @param type - the type to match
 */


function elementIsOfType(element, type) {
  return element.type === type;
}
/**
 * This utility maps through the SSR element and injects break tags into all
 * empty p tags.
 *
 * @remarks
 *
 * Prosemirror automatically injects break tags into empty paragraph tags. This
 * causes the document rendered during SSR to be different than when the page
 * loads.
 */


function injectBrIntoEmptyParagraphs(element) {
  return cloneSSRElement(element, children => {
    if (!isArray(children)) {
      return children;
    }

    return Children.map(children, child => {
      if (!(isReactDOMElement(child) && elementIsEmpty(child) && elementIsOfType(child, 'p'))) {
        return child;
      }

      var properties = getElementProps(child);
      return /*#__PURE__*/cloneElement(child, properties, /*#__PURE__*/createElement('br'));
    });
  });
}

export { ReactSsrExtension };
